import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Receipt, Plus, Eye, Pencil, Trash2, Check, X, RotateCcw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import Alert from '../common/Alert.js';
import expenseService from '../../services/ExpenseService.js';
import {
  PageLayout,
  Button,
  DataTable,
  FilterPanel,
  Pagination,
  StatusBadge,
} from '../common/index.js';

const ExpenseList = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterValues, setFilterValues] = useState({
    category: '',
    month: '',
    year: '',
    status: '',
    startDate: '',
    endDate: '',
  });
  const navigate = useNavigate();
  const { user } = useAuth();
  const perPage = 10;

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);
  const statuses = ['pending', 'approved', 'paid', 'rejected', 'cancelled'];

  useEffect(() => {
    fetchDropdownData();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchExpenses(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filterValues]);

  useEffect(() => {
    fetchExpenses(currentPage);
  }, [currentPage]);

  const fetchDropdownData = async () => {
    try {
      const [categoriesRes, monthsRes] = await Promise.all([
        expenseService.getCategories(),
        expenseService.getMonths(),
      ]);
      setCategories(categoriesRes.data || []);
      setMonths(monthsRes.data || []);
    } catch (err) {
      console.error('Failed to load dropdown data', err);
    }
  };

  const fetchExpenses = async (page = currentPage) => {
    try {
      setLoading(true);
      const filters = {};
      if (filterValues.category) filters.category = filterValues.category;
      if (filterValues.month) filters.month = filterValues.month;
      if (filterValues.year) filters.year = filterValues.year;
      if (filterValues.status) filters.status = filterValues.status;
      if (filterValues.startDate) filters.start_date = filterValues.startDate;
      if (filterValues.endDate) filters.end_date = filterValues.endDate;

      const response = await expenseService.getAll(page, perPage, filters);
      setExpenses(response.data);
      setTotalPages(response.pagination.pages);
    } catch (err) {
      Alert.error('Failed to load expenses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (expenseId, expenseName) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete expense "${expenseName}"?`,
      'Delete Expense'
    );

    if (confirmed) {
      try {
        await expenseService.delete(expenseId);
        Alert.success('Expense deleted successfully!');

        if (expenses.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchExpenses();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete expense');
      }
    }
  };

  const handleApprove = async (expenseId) => {
    const confirmed = await Alert.confirm(
      'Are you sure you want to approve this expense?',
      'Approve Expense'
    );

    if (confirmed) {
      try {
        await expenseService.approve(expenseId);
        Alert.success('Expense approved successfully!');
        fetchExpenses();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to approve expense');
      }
    }
  };

  const handleReject = async (expenseId) => {
    const confirmed = await Alert.confirm(
      'Are you sure you want to reject this expense?',
      'Reject Expense'
    );
    if (confirmed) {
      try {
        await expenseService.update(expenseId, { status: 'rejected' });
        Alert.success('Expense rejected successfully!');
        fetchExpenses();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to reject expense');
      }
    }
  };

  const handleSetPending = async (expenseId) => {
    const confirmed = await Alert.confirm(
      'Are you sure you want to set this expense back to pending?',
      'Set to Pending'
    );
    if (confirmed) {
      try {
        await expenseService.update(expenseId, { status: 'pending' });
        Alert.success('Expense set to pending!');
        fetchExpenses();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to update status');
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatAmount = (amount) => `₹${parseFloat(amount).toFixed(2)}`;

  const columns = [
    {
      key: 'expense_id',
      label: 'Expense ID',
      render: (expense) => (
        <button
          type="button"
          onClick={() => navigate(`/expenses/view/${expense.expense_id}`)}
          className="font-medium text-brand hover:underline"
        >
          {expense.expense_id}
        </button>
      ),
    },
    { key: 'category_display', label: 'Category' },
    {
      key: 'expense_date',
      label: 'Date',
      render: (expense) => formatDate(expense.expense_date),
    },
    {
      key: 'amount',
      label: 'Amount',
      render: (expense) => formatAmount(expense.amount),
    },
    {
      key: 'vendor_name',
      label: 'Vendor',
      render: (expense) => expense.vendor_name || 'N/A',
    },
    {
      key: 'status',
      label: 'Status',
      render: (expense) => <StatusBadge status={expense.status} label={expense.status_display} />,
    },
    { key: 'created_by', label: 'Created By' },
  ];

  const getActions = (expense) => [
    {
      label: 'View Details',
      icon: Eye,
      onClick: () => navigate(`/expenses/view/${expense.expense_id}`),
    },
    {
      label: 'Edit',
      icon: Pencil,
      onClick: () => navigate(`/expenses/edit/${expense.expense_id}`),
    },
    {
      label: 'Approve',
      icon: Check,
      variant: 'success',
      visible: expense.status === 'pending',
      onClick: () => handleApprove(expense.expense_id),
    },
    {
      label: 'Reject',
      icon: X,
      variant: 'warning',
      visible: expense.status === 'pending' || expense.status === 'approved',
      onClick: () => handleReject(expense.expense_id),
    },
    {
      label: 'Set Pending',
      icon: RotateCcw,
      visible: expense.status === 'rejected' || expense.status === 'approved',
      onClick: () => handleSetPending(expense.expense_id),
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: () => handleDelete(expense.expense_id, expense.category_display),
    },
  ];

  return (
    <PageLayout
      title="Expenses"
      icon={Receipt}
      actions={
        <Button icon={Plus} onClick={() => navigate('/expenses/add')}>
          Add Expense
        </Button>
      }
    >
      <FilterPanel
        filters={[
          {
            key: 'category',
            label: 'Category',
            type: 'select',
            options: categories.map((cat) => ({ value: cat.value, label: cat.label })),
          },
          {
            key: 'month',
            label: 'Month',
            type: 'select',
            options: months.map((month) => ({ value: month.value, label: month.label })),
          },
          {
            key: 'year',
            label: 'Year',
            type: 'select',
            options: years.map((year) => ({ value: String(year), label: String(year) })),
          },
          {
            key: 'status',
            label: 'Status',
            type: 'select',
            options: statuses.map((status) => ({
              value: status,
              label: status.charAt(0).toUpperCase() + status.slice(1),
            })),
          },
          { key: 'startDate', label: 'Start Date', type: 'date' },
          { key: 'endDate', label: 'End Date', type: 'date' },
        ]}
        values={filterValues}
        onChange={setFilterValues}
        onClear={() =>
          setFilterValues({
            category: '',
            month: '',
            year: '',
            status: '',
            startDate: '',
            endDate: '',
          })
        }
      />

      <DataTable
        columns={columns}
        data={expenses}
        loading={loading}
        emptyMessage="No expenses found"
        rowKey="expense_id"
        actions={getActions}
      />

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </PageLayout>
  );
};

export default ExpenseList;
